import { format, parseISO, isValid } from 'date-fns';
import { ro } from 'date-fns/locale';
import { PatientGender } from '@/types/patient.types';
import { UserLevel } from '@/types/auth.types';
import { HealthMarkerType } from '@/types/healthMarker.types';
import { 
  GENDER_OPTIONS, 
  USER_LEVELS, 
  HEALTH_MARKER_TYPES, 
  SEVERITY_LEVELS 
} from './constants';

// Date formatting functions
export const formatDate = (dateString: string, formatString: string = 'dd.MM.yyyy'): string => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    if (!isValid(date)) {
      return 'Data invalidă';
    }
    return format(date, formatString, { locale: ro });
  } catch (error) {
    return 'Data invalidă';
  }
};

export const formatDateTime = (dateString: string): string => {
  return formatDate(dateString, 'dd.MM.yyyy HH:mm');
};

export const formatTimeAgo = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Acum';
    if (diffInMinutes < 60) return `${diffInMinutes} min în urmă`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} ore în urmă`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} zile în urmă`;
    
    return formatDate(dateString);
  } catch (error) {
    return 'Data invalidă';
  }
};

export const formatInputDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    if (!isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    return '';
  }
};

// Number formatting functions
export const formatNumber = (value: number, decimals: number = 0): string => {
  if (isNaN(value)) return '0';
  return new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

export const formatWeight = (grams: number): string => {
  if (grams >= 1000) {
    return `${formatNumber(grams / 1000, 1)} kg`;
  }
  return `${formatNumber(grams)} g`;
};

export const formatGestationalAge = (weeks: number): string => {
  const wholeWeeks = Math.floor(weeks);
  const days = Math.round((weeks - wholeWeeks) * 7);
  
  if (days === 0) {
    return `${wholeWeeks} săpt.`;
  }
  return `${wholeWeeks}+${days} săpt.`;
};

export const formatPercentage = (value: number, decimals: number = 1): string => {
  if (isNaN(value)) return '0%';
  return `${formatNumber(value, decimals)}%`;
};

// Gender formatting
export const formatGender = (gender: PatientGender): string => {
  return GENDER_OPTIONS[gender] || 'Necunoscut';
};

// User level formatting
export const formatUserLevel = (level: UserLevel): string => {
  return USER_LEVELS[level] || 'Necunoscut';
};

// Health marker formatting
export const formatHealthMarker = (
  type: HealthMarkerType, 
  value: number | string,
  unit?: string
): string => {
  const markerConfig = HEALTH_MARKER_TYPES[type];
  if (!markerConfig) return String(value);
  
  if (markerConfig.isNumeric && typeof value === 'number') {
    const displayUnit = unit || markerConfig.unit;
    return `${formatNumber(value, 1)} ${displayUnit}`.trim();
  }
  
  return String(value);
};

export const formatHealthMarkerType = (type: HealthMarkerType): string => {
  return HEALTH_MARKER_TYPES[type]?.name || 'Necunoscut';
};

// Severity level formatting
export const formatSeverityLevel = (level: number): string => {
  return SEVERITY_LEVELS[level as keyof typeof SEVERITY_LEVELS]?.label || 'Necunoscut';
};

export const getSeverityColor = (level: number): { color: string; bgColor: string } => {
  const severity = SEVERITY_LEVELS[level as keyof typeof SEVERITY_LEVELS];
  return {
    color: severity?.color || 'text-gray-600',
    bgColor: severity?.bgColor || 'bg-gray-100',
  };
};

// Patient code formatting
export const formatPatientCode = (code: string): string => {
  // Add visual formatting to patient codes if needed
  return code.toUpperCase();
};

// ICD-10 code formatting
export const formatICD10Code = (code: string): string => {
  // Ensure proper format: Q21.0
  if (code.length >= 3 && code.indexOf('.') === -1) {
    return `${code.slice(0, 3)}.${code.slice(3)}`;
  }
  return code.toUpperCase();
};

// File size formatting
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Currency formatting (if needed for costs)
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: 'RON',
  }).format(amount);
};

// Phone number formatting
export const formatPhoneNumber = (phone: string): string => {
  // Basic Romanian phone number formatting
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  
  return phone;
};

// Trim text with ellipsis
export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
};

// Capitalize first letter
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Format boolean as Romanian yes/no
export const formatBoolean = (value: boolean | undefined | null): string => {
  if (value === null || value === undefined) return 'Necunoscut';
  return value ? 'Da' : 'Nu';
};

// Age calculation
export const calculateAge = (birthDate: string): number => {
  try {
    const birth = parseISO(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    return 0;
  }
};

export const formatAge = (birthDate: string): string => {
  const age = calculateAge(birthDate);
  if (age === 0) return 'Sub 1 an';
  if (age === 1) return '1 an';
  return `${age} ani`;
};